import { Decimal } from '@paguz/contracts';
import { Button, Card, CardContent, Separator, Skeleton } from '@paguz/ui';
import { Link } from '@tanstack/react-router';
import {
  CalendarClockIcon,
  ChevronRightIcon,
  FileTextIcon,
  QrCodeIcon,
  ReceiptTextIcon,
  SparklesIcon,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { QueryErrorState } from '../../components/feedback.tsx';
import { BrandHeader } from '../../components/layout.tsx';
import { PrivateMoney } from '../../components/money.tsx';
import { firstName, formatLongDate } from '../../lib/format.ts';
import { CreditLineCard } from '../credit/credit-line-card.tsx';
import { useMovements, usePrimaryLine } from '../credit/hooks.ts';
import { MovementRow } from '../credit/movement-row.tsx';
import { NoLineState } from '../credit/no-line-state.tsx';
import { PayInstallmentSheet } from '../credit/pay-installment-sheet.tsx';
import { ApplicationHomeCard } from '../origination/application-home-card.tsx';
import { FirstRunTour } from '../guide/first-run-tour.tsx';
import { useMe } from '../profile/use-me.ts';

const QUICK_ACTIONS: readonly {
  to: '/pagar' | '/asesor' | '/movimientos' | '/extracto';
  label: string;
  icon: LucideIcon;
}[] = [
  { to: '/pagar', label: 'Pagar con QR', icon: QrCodeIcon },
  { to: '/asesor', label: 'Asesor IA', icon: SparklesIcon },
  { to: '/movimientos', label: 'Movimientos', icon: ReceiptTextIcon },
  { to: '/extracto', label: 'Extracto', icon: FileTextIcon },
];

export function HomePage() {
  const me = useMe();
  const line = usePrimaryLine();
  const lineId = line.data?.id;
  const recent = useMovements(lineId, 'ALL', 3);
  const [payOpen, setPayOpen] = useState(false);

  return (
    <>
      <BrandHeader />
      <FirstRunTour />
      <div className="mb-5">
        {me.data ? (
          <>
            <h1 className="text-2xl">Hola, {firstName(me.data.fullName)}</h1>
            {me.data.businessName !== null && (
              <p className="text-muted-foreground">{me.data.businessName}</p>
            )}
          </>
        ) : (
          <Skeleton className="h-8 w-40" />
        )}
      </div>

      {line.isPending ? (
        <Skeleton className="h-48 w-full rounded-2xl" />
      ) : line.isError ? (
        <QueryErrorState error={line.error} onRetry={() => void line.refetch()} />
      ) : line.data === null ? (
        me.data?.activeApplicationId ? (
          <ApplicationHomeCard />
        ) : (
          <NoLineState />
        )
      ) : (
        <div className="flex flex-col gap-5">
          <CreditLineCard line={line.data} />

          {line.data.nextDueDate !== null && new Decimal(line.data.minimumPayment.amount).gt(0) && (
            <Card className="gap-3 py-4">
              <CardContent className="flex flex-col gap-3 px-4">
                <div className="flex items-center gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                    <CalendarClockIcon className="size-5" aria-hidden />
                  </span>
                  <div className="flex flex-1 flex-col">
                    <span className="text-sm text-muted-foreground">Tu próximo pago</span>
                    <PrivateMoney value={line.data.minimumPayment.amount} size="lg" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {line.data.dpd > 0 ? 'Venció el ' : 'Paga a más tardar el '}
                  <strong className="text-foreground">
                    {formatLongDate(line.data.nextDueDate)}
                  </strong>
                </p>
                <Button
                  variant={line.data.dpd > 0 ? 'default' : 'secondary'}
                  onClick={() => {
                    setPayOpen(true);
                  }}
                >
                  Pagar mi cuota
                </Button>
              </CardContent>
            </Card>
          )}

          <nav aria-label="Accesos rápidos">
            <ul className="grid grid-cols-4 gap-2">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <li key={action.to}>
                    <Link
                      to={action.to}
                      className="flex flex-col items-center gap-1.5 rounded-xl p-1 text-center text-xs font-medium hover:bg-accent"
                    >
                      <span
                        className={
                          action.to === '/pagar'
                            ? 'flex size-12 items-center justify-center rounded-2xl bg-highlight text-highlight-foreground'
                            : 'flex size-12 items-center justify-center rounded-2xl bg-secondary text-secondary-foreground'
                        }
                      >
                        <Icon className="size-6" aria-hidden />
                      </span>
                      {action.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <section aria-labelledby="recent-title">
            <div className="mb-1 flex items-center justify-between">
              <h2 id="recent-title" className="text-lg">
                Últimos movimientos
              </h2>
              <Link
                to="/movimientos"
                className="flex min-h-11 items-center text-sm font-semibold text-primary"
              >
                Ver todos
                <ChevronRightIcon className="size-4" aria-hidden />
              </Link>
            </div>
            {recent.isPending ? (
              <Skeleton className="h-40 w-full rounded-lg" />
            ) : recent.isError ? (
              <QueryErrorState error={recent.error} onRetry={() => void recent.refetch()} />
            ) : recent.data.pages[0]?.data.length === 0 ? (
              <p className="py-4 text-muted-foreground">
                Aún no tienes movimientos. ¡Haz tu primera compra con QR!
              </p>
            ) : (
              <ul>
                {(recent.data.pages[0]?.data ?? []).map((movement, index) => (
                  <Fragment key={movement.id}>
                    {index > 0 && <Separator />}
                    <li>
                      <MovementRow movement={movement} />
                    </li>
                  </Fragment>
                ))}
              </ul>
            )}
          </section>

          <PayInstallmentSheet line={line.data} open={payOpen} onOpenChange={setPayOpen} />
        </div>
      )}
    </>
  );
}
